import React, { useState, forwardRef, useEffect } from 'react';

// FloatingInput.jsx
// A reusable input component with a floating label effect.
// It uses Bootstrap's form-floating class and custom styles for label behavior.
const FloatingInput = forwardRef(({ label, type = 'text', required = false, name, value, onChange }, ref) => {
    return (
        <div className="form-floating mb-3">
            {/* Input field with Bootstrap styling */}
            <input
                type={type}
                name={name}
                className="form-control"
                placeholder=" " // Placeholder is required for form-floating to work correctly
                value={value}
                onChange={onChange}
                required={required}
                ref={ref} // Forwarding ref to the input element
            />
            {/* Label for the input field */}
            <label>{label}</label>
            {/* Custom styles for the floating label and autofill behavior */}
            <style>{`
                .form-floating > .form-control:focus ~ label::after,
                .form-floating > .form-control:not(:placeholder-shown) ~ label::after {
                    background-color: transparent; /* Prevents background on label when focused/filled */
                }
                .form-floating > .form-control:focus ~ label,
                .form-floating > .form-control:not(:placeholder-shown) ~ label {
                    color: #0C5546; /* A distinct color for active/filled label */
                }
                input:-webkit-autofill {
                    box-shadow: 0 0 0px 1000px transparent inset !important;
                    -webkit-box-shadow: 0 0 0px 1000px transparent inset !important;
                    -webkit-text-fill-color: black !important;
                    transition: background-color 5000s ease-in-out 0s;
                }
            `}</style>
        </div>
    );
});

// ResumeBuilder.jsx
// The main component for building and previewing a resume.
const ResumeBuilder = () => {
    // Initial state pre-filled with example data for demonstration.
    // This data is used if no saved data is found in localStorage.
    const initialResumeData = {
        personalInfo: {
            name: 'Herman Walton',
            title: 'Junior Student',
            address: '1200 Patterno Avenue, New York, NY 10471, United States',
            phone: '(917)324-1818',
            email: 'hw_alton77_x@yahoo.com',
            imageUrl: 'https://placehold.co/150x150/E0E0E0/000000?text=Profile' // Placeholder profile image
        },
        profileSummary: 'Ambitious Sales Assistant with foundational experience in customer service and retail environments. Skilled in recommending products and maintaining organizational standards, while collaborating effectively with team members. Adept at utilizing basic computer skills and eager to expand knowledge in sales techniques and communication. Committed to delivering exceptional service and contributing positively to team objectives.',
        skills: ['Attention to Detail', 'Yarn', 'Team Collaboration', 'Organizational Skills', 'Introductory Sales Techniques', 'Basic Communication Skills', 'Customer Service'],
        languages: ['English', 'French', 'Dutch'],
        employmentHistory: [
            {
                title: 'Entry Level Sales Assistant',
                company: 'Big Apple Bookstore',
                startDate: 'September 2015',
                endDate: 'June 2018',
                responsibilities: [
                    'Welcomed customers and supported them in locating books',
                    'Learned to offer book recommendations based on customer interests',
                    'Followed guidance from supervisors to complete tasks accurately',
                    'Assisted in organizing books and maintaining store policies'
                ]
            }
        ],
        education: [
            {
                degree: 'Bachelor of Communications',
                institution: 'New York University, New York',
                startDate: 'August 2016',
                endDate: 'August 2021',
                details: ['Working towards a Communications Degree.']
            },
            {
                degree: 'High School Diploma',
                institution: 'Regis High School, New York',
                startDate: 'September 2012',
                endDate: 'May 2016',
                details: ['Graduated with High Honors']
            }
        ]
    };

    // Initialize state with data from localStorage or initialResumeData.
    // This ensures that previously entered data is loaded when the component mounts.
    const [resumeData, setResumeData] = useState(() => {
        try {
            const savedData = localStorage.getItem('resumeData');
            return savedData ? JSON.parse(savedData) : initialResumeData;
        } catch (error) {
            console.error("Error parsing resume data from localStorage:", error);
            return initialResumeData; // Fallback to initial data if parsing fails
        }
    });

    // State to track if html2pdf.js has loaded
    const [isHtml2PdfLoaded, setIsHtml2PdfLoaded] = useState(false);

    // useEffect hook to save resumeData to localStorage whenever it changes.
    // This provides data persistence across browser sessions.
    useEffect(() => {
        try {
            localStorage.setItem('resumeData', JSON.stringify(resumeData));
        } catch (error) {
            console.error("Error saving resume data to localStorage:", error);
        }
    }, [resumeData]); // Dependency array ensures effect runs only when resumeData changes

    // useEffect hook to dynamically load html2pdf.js
    useEffect(() => {
        const scriptId = 'html2pdf-script';
        if (!document.getElementById(scriptId)) {
            const script = document.createElement('script');
            script.src = "https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js";
            script.id = scriptId;
            script.onload = () => {
                setIsHtml2PdfLoaded(true);
                console.log("html2pdf.js loaded successfully.");
            };
            script.onerror = () => {
                console.error("Failed to load html2pdf.js script.");
            };
            document.body.appendChild(script);
        } else if (window.html2pdf) {
            // If script already exists and html2pdf is available (e.g., on re-render), set loaded state
            setIsHtml2PdfLoaded(true);
        }

        // Cleanup function to remove the script when the component unmounts
        return () => {
            const script = document.getElementById(scriptId);
            if (script) {
                script.remove();
            }
        };
    }, []); // Empty dependency array ensures this runs only once on mount

    // handleChange: Generic handler for updating simple text fields (personal info, profile summary).
    // It handles both top-level state properties and nested properties (e.g., personalInfo.name).
    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name.includes('.')) {
            // If the name contains a dot, it's a nested property (e.g., "personalInfo.name")
            const [parent, child] = name.split('.');
            setResumeData(prevData => ({
                ...prevData,
                [parent]: {
                    ...prevData[parent],
                    [child]: value
                }
            }));
        } else {
            // Otherwise, it's a top-level property (e.g., "profileSummary")
            setResumeData(prevData => ({
                ...prevData,
                [name]: value
            }));
        }
    };

    // handleArrayChange: Handler for updating array fields like skills and languages.
    // It splits the input string by comma or pipe and filters out empty entries.
    const handleArrayChange = (e) => {
        const { name, value } = e.target;
        setResumeData(prevData => ({
            ...prevData,
            [name]: value.split(/[,|]\s*/).filter(item => item.trim() !== '') // Splits by comma or pipe, trims whitespace, and removes empty strings
        }));
    };

    // handleAddEntry: Adds a new empty entry to an array section (employmentHistory or education).
    const handleAddEntry = (section) => {
        setResumeData(prevData => ({
            ...prevData,
            [section]: [...prevData[section], section === 'employmentHistory' ? {
                // Default structure for a new employment entry
                title: '',
                company: '',
                startDate: '',
                endDate: '',
                responsibilities: [''] // Start with one empty responsibility
            } : {
                // Default structure for a new education entry
                degree: '',
                institution: '',
                startDate: '',
                endDate: '',
                details: [''] // Start with one empty detail
            }]
        }));
    };

    // handleRemoveEntry: Removes an entry from an array section based on its index.
    const handleRemoveEntry = (section, index) => {
        setResumeData(prevData => ({
            ...prevData,
            [section]: prevData[section].filter((_, i) => i !== index) // Filters out the entry at the specified index
        }));
    };

    // handleNestedChange: Handles changes for fields within nested array objects (e.g., title of an employment entry).
    const handleNestedChange = (section, index, e) => {
        const { name, value } = e.target;
        setResumeData(prevData => {
            const updatedSection = [...prevData[section]]; // Create a shallow copy of the section array
            updatedSection[index] = {
                ...updatedSection[index], // Create a shallow copy of the specific entry
                [name]: value // Update the specific field within that entry
            };
            return {
                ...prevData,
                [section]: updatedSection // Return the updated state
            };
        });
    };

    // handleBulletChange: Handles changes for individual bullet points (responsibilities or details).
    const handleBulletChange = (section, entryIndex, bulletIndex, e) => {
        const { value } = e.target;
        setResumeData(prevData => {
            const updatedSection = [...prevData[section]]; // Copy the section array
            const bulletFieldName = section === 'employmentHistory' ? 'responsibilities' : 'details'; // Determine the correct field name
            const updatedBullets = [...updatedSection[entryIndex][bulletFieldName]]; // Copy the bullets array
            updatedBullets[bulletIndex] = value; // Update the specific bullet point
            updatedSection[entryIndex] = {
                ...updatedSection[entryIndex],
                [bulletFieldName]: updatedBullets // Assign the updated bullets back to the entry
            };
            return {
                ...prevData,
                [section]: updatedSection
            };
        });
    };

    // handleAddBullet: Adds a new empty bullet point to an entry's responsibilities or details.
    const handleAddBullet = (section, entryIndex) => {
        setResumeData(prevData => {
            const updatedSection = [...prevData[section]];
            const bulletFieldName = section === 'employmentHistory' ? 'responsibilities' : 'details';
            const bullets = updatedSection[entryIndex][bulletFieldName];
            updatedSection[entryIndex] = {
                ...updatedSection[entryIndex],
                [bulletFieldName]: [...bullets, ''] // Add an empty string for a new bullet
            };
            return {
                ...prevData,
                [section]: updatedSection
            };
        });
    };

    // handleRemoveBullet: Removes a bullet point from an entry's responsibilities or details.
    const handleRemoveBullet = (section, entryIndex, bulletIndex) => {
        setResumeData(prevData => {
            const updatedSection = [...prevData[section]];
            const bulletFieldName = section === 'employmentHistory' ? 'responsibilities' : 'details';
            const bullets = updatedSection[entryIndex][bulletFieldName];
            updatedSection[entryIndex] = {
                ...updatedSection[entryIndex],
                [bulletFieldName]: bullets.filter((_, i) => i !== bulletIndex) // Filter out the bullet at the given index
            };
            return {
                ...prevData,
                [section]: updatedSection
            };
        });
    };

    // handleDownloadPdf: Function to generate and download the resume as a PDF.
    // It uses the html2pdf.js library, which must be loaded via a CDN.
    const handleDownloadPdf = () => {
        // Ensure the latest data is saved before PDF generation
        localStorage.setItem('resumeData', JSON.stringify(resumeData));

        const resumeContent = document.getElementById('resume-preview-content'); // Get the resume preview HTML element
        if (resumeContent && window.html2pdf) { // Ensure html2pdf is available
            // Configuration options for html2pdf
            const opt = {
                // margin: 0, // Page margins in inches
                margin: [0.2, 0, 0, 0],
                filename: `${resumeData.personalInfo.name.replace(/\s/g, '_')}_Resume.pdf`, // Dynamic filename
                image: { type: 'jpeg', quality: 0.98 }, // Image quality for canvas rendering
                html2canvas: { scale: 2, logging: true, dpi: 300, letterRendering: true }, // html2canvas options for better rendering
                jsPDF: { unit: 'cm', format: 'a4', orientation: 'portrait' } // jsPDF options for page format and orientation
            };
            // Call html2pdf to generate and save the PDF
            window.html2pdf().set(opt).from(resumeContent).save();
        } else {
            console.error("Resume content element not found or html2pdf.js not loaded for PDF generation.");
            // Optionally, provide user feedback here, e.g., a message box
        }
    };

    return (
        <div className="resume-builder container-fluid">
            <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css" />
            <style>{`
                .resume-builder {
                    padding: 20px;
                    // font-family: 'Inter', sans-serif; /* Using Inter font */
                }
                .form-section {
                    background-color: #ffffff;
                    border-radius: 8px;
                    box-shadow: 0 4px 8px rgba(0,0,0,0.05);
                    padding: 20px;
                    margin-bottom: 30px;
                }
                .resume-preview-container {
                    background-color: #ffffff;
                    border-radius: 8px;
                    box-shadow: 0 4px 8px rgba(0,0,0,0.05);
                    padding: 22px;
                    margin-right: 15px;
                    min-height: 1100px; /* Adjust as needed */
                    border: 1px solid #e0e0e0;
                    overflow: hidden; /* To contain floated elements */
                }

                /* Resume Specific Styles */
                .resume-header-section {
                    background-color: #6ee7b7; /* Green background from image */
                    padding: 20px;
                    display: flex;
                    align-items: center;
                    color: #000;
                    margin-bottom: 20px;
                    border-radius: 8px;
                    overflow: hidden; /* Ensure content stays within */
                }
                .resume-header-section img {
                    width: 150px;
                    height: 150px;
                    border-radius: 8px; /* Slightly rounded corners for image */
                    object-fit: cover;
                    margin-right: 20px;
                    border: 2px solid rgba(0,0,0,0.1);
                }
                .resume-header-info h2 {
                    font-size: 2.5rem;
                    font-weight: 700;
                    margin-bottom: 5px;
                }
                .resume-header-info h3 {
                    font-size: 1.5rem;
                    font-weight: 500;
                    margin-bottom: 10px;
                    color: #333;
                }
                .resume-header-info p {
                    font-size: 0.9rem;
                    margin-bottom: 3px;
                    color: #444;
                }

                .resume-content {
                    display: flex;
                    flex-wrap: wrap; /* Allow wrapping on smaller screens */
                }
                .resume-left-column {
                    flex: 0 0 35%; /* 30% width */
                    max-width: 35%;
                    padding-right: 20px;
                    border-right: 1px solid #eee; /* Separator line */
                }
                .resume-right-column {
                    flex: 0 0 65%; /* 70% width */
                    max-width: 65%;
                    padding-left: 20px;
                }

                .resume-section-title {
                    font-size: 1.2rem;
                    font-weight: 600;
                    margin-bottom: 15px;
                    color: #0C5546; /* Dark green for titles */
                    border-bottom: 2px solid #eee;
                    padding-bottom: 5px;
                }
                .resume-section ul {
                    list-style: none;
                    padding-left: 0;
                }
                .resume-section ul li {
                    margin-bottom: 8px;
                    font-size: 0.95rem;
                    color: #555;
                }
                .resume-section ul li::before {
                    content: "•";
                    color: #0C5546;
                    display: inline-block;
                    width: 1em;
                    margin-left: -1em;
                }

                .employment-item, .education-item {
                    margin-bottom: 20px;
                }
                .employment-item h4, .education-item h4 {
                    font-size: 1.1rem;
                    font-weight: 600;
                    margin-bottom: 5px;
                    color: #333;
                }
                .employment-item p, .education-item p {
                    font-size: 0.9rem;
                    color: #666;
                    margin-bottom: 5px;
                }
                .employment-item ul, .education-item ul {
                    list-style: disc;
                    padding-left: 20px;
                }
                .employment-item ul li, .education-item ul li {
                    font-size: 0.9rem;
                    margin-bottom: 3px;
                    color: #555;
                }

                .profile-summary-text {
                    font-size: 0.95rem;
                    line-height: 1.6;
                    color: #444;
                    margin-bottom: 20px;
                }

                .form-control:focus {
                    border-color: #6ee7b7;
                    box-shadow: 0 0 0 0.25rem rgba(110, 231, 183, 0.25);
                }
                .btn-primary {
                    background-color: #0C5546;
                    border-color: #0C5546;
                }
                .btn-primary:hover {
                    background-color: #094035;
                    border-color: #094035;
                }
                .btn-outline-primary {
                    color: #0C5546;
                    border-color: #0C5546;
                }
                .btn-outline-primary:hover {
                    background-color: #0C5546;
                    color: #fff;
                }
                .btn-danger {
                    background-color: #dc3545;
                    border-color: #dc3545;
                }
                .btn-danger:hover {
                    background-color: #c82333;
                    border-color: #bd2130;
                }
                .btn-outline-danger {
                    color: #dc3545;
                    margin-bottom: 15px;
                    border-color: #dc3545;
                }
                .btn-outline-danger:hover {
                    background-color: #dc3545;
                    color: #fff;
                }
                .btn-success {
                    background-color: #28a745;
                    border-color: #28a745;
                }
                .btn-success:hover {
                    background-color: #218838;
                    border-color: #1e7e34;
                }


                /* Responsive adjustments */
                @media (max-width: 991.98px) {
                    .resume-left-column, .resume-right-column {
                        flex: 0 0 100%;
                        max-width: 100%;
                        padding-left: 0;
                        padding-right: 0;
                        border-right: none;
                    }
                    .resume-left-column {
                        margin-bottom: 20px;
                    }
                    .resume-header-section {
                        flex-direction: column;
                        text-align: center;
                    }
                    .resume-header-section img {
                        margin-right: 0;
                        margin-bottom: 15px;
                    }
                }
            `}</style>

            <div className="row mt-4">
                {/* Input Form Section */}
                <div className="col-lg-6">
                    <div className="form-section">
                        <h3 className="mb-4 text-center">Build Your Resume</h3>

                        {/* Personal Information */}
                        <h5 className="mt-4 mb-3 text-primary">Personal Information</h5>
                        <FloatingInput label="Full Name" name="personalInfo.name" value={resumeData.personalInfo.name} onChange={handleChange} required />
                        <FloatingInput label="Title (e.g., Junior Student, Software Engineer)" name="personalInfo.title" value={resumeData.personalInfo.title} onChange={handleChange} required />
                        <FloatingInput label="Address" name="personalInfo.address" value={resumeData.personalInfo.address} onChange={handleChange} />
                        <FloatingInput label="Phone" name="personalInfo.phone" value={resumeData.personalInfo.phone} onChange={handleChange} />
                        <FloatingInput label="Email" name="personalInfo.email" value={resumeData.personalInfo.email} onChange={handleChange} type="email" />
                        <FloatingInput label="Profile Image URL" name="personalInfo.imageUrl" value={resumeData.personalInfo.imageUrl} onChange={handleChange} type="url" />

                        {/* Profile Summary */}
                        <h5 className="mt-4 mb-3 text-primary">Profile Summary</h5>
                        <div className="form-floating mb-3">
                            <textarea
                                className="form-control"
                                placeholder=" "
                                id="profileSummary"
                                name="profileSummary"
                                value={resumeData.profileSummary}
                                onChange={handleChange}
                                style={{ height: '120px' }}
                            ></textarea>
                            <label htmlFor="profileSummary">Profile Summary</label>
                        </div>

                        {/* Skills */}
                        <h5 className="mt-4 mb-3 text-primary">Skills (Comma or Pipe Separated)</h5>
                        <FloatingInput label="Skills (e.g., HTML, CSS | JavaScript)" name="skills" value={resumeData.skills.join(' | ')} onChange={handleArrayChange} />

                        {/* Languages */}
                        <h5 className="mt-4 mb-3 text-primary">Languages (Comma or Pipe Separated)</h5>
                        <FloatingInput label="Languages (e.g., English, French | Spanish)" name="languages" value={resumeData.languages.join(' | ')} onChange={handleArrayChange} />

                        {/* Employment History */}
                        <h5 className="mt-4 mb-3 text-primary">Employment History</h5>
                        {resumeData.employmentHistory.map((entry, index) => (
                            <div key={index} className="border p-3 mb-3 rounded position-relative">
                                <h6 className="mb-3">Employment #{index + 1}</h6>
                                <FloatingInput label="Job Title" name="title" value={entry.title} onChange={(e) => handleNestedChange('employmentHistory', index, e)} required />
                                <FloatingInput label="Company" name="company" value={entry.company} onChange={(e) => handleNestedChange('employmentHistory', index, e)} required />
                                <div className="row">
                                    <div className="col-md-6">
                                        <FloatingInput label="Start Date" name="startDate" value={entry.startDate} onChange={(e) => handleNestedChange('employmentHistory', index, e)} />
                                    </div>
                                    <div className="col-md-6">
                                        <FloatingInput label="End Date" name="endDate" value={entry.endDate} onChange={(e) => handleNestedChange('employmentHistory', index, e)} />
                                    </div>
                                </div>
                                <h6 className="mt-3 mb-2">Responsibilities</h6>
                                {entry.responsibilities.map((bullet, bIndex) => (
                                    <div key={bIndex} className="input-group mb-2">
                                        <FloatingInput label={`Responsibility ${bIndex + 1}`} name="responsibility" value={bullet} onChange={(e) => handleBulletChange('employmentHistory', index, bIndex, e)} />
                                        <button className="btn btn-outline-danger" type="button" onClick={() => handleRemoveBullet('employmentHistory', index, bIndex)}>
                                            <i className="fas fa-minus"></i>
                                        </button>
                                    </div>
                                ))}
                                <button className="btn btn-sm btn-outline-primary mt-2" onClick={() => handleAddBullet('employmentHistory', index)}>
                                    Add Responsibility
                                </button>
                                {resumeData.employmentHistory.length > 1 && (
                                    <button className="btn btn-sm btn-danger position-absolute top-0 end-0 m-2" onClick={() => handleRemoveEntry('employmentHistory', index)}>
                                        <i className="fas fa-times"></i>
                                    </button>
                                )}
                            </div>
                        ))}
                        <button className="btn btn-primary mt-3" onClick={() => handleAddEntry('employmentHistory')}>
                            Add Employment
                        </button>

                        {/* Education */}
                        <h5 className="mt-4 mb-3 text-primary">Education</h5>
                        {resumeData.education.map((entry, index) => (
                            <div key={index} className="border p-3 mb-3 rounded position-relative">
                                <h6 className="mb-3">Education #{index + 1}</h6>
                                <FloatingInput label="Degree/Program" name="degree" value={entry.degree} onChange={(e) => handleNestedChange('education', index, e)} required />
                                <FloatingInput label="Institution" name="institution" value={entry.institution} onChange={(e) => handleNestedChange('education', index, e)} required />
                                <div className="row">
                                    <div className="col-md-6">
                                        <FloatingInput label="Start Date" name="startDate" value={entry.startDate} onChange={(e) => handleNestedChange('education', index, e)} />
                                    </div>
                                    <div className="col-md-6">
                                        <FloatingInput label="End Date" name="endDate" value={entry.endDate} onChange={(e) => handleNestedChange('education', index, e)} />
                                    </div>
                                </div>
                                <h6 className="mt-3 mb-2">Details</h6>
                                {entry.details.map((bullet, bIndex) => (
                                    <div key={bIndex} className="input-group mb-2">
                                        <FloatingInput label={`Detail ${bIndex + 1}`} name="detail" value={bullet} onChange={(e) => handleBulletChange('education', index, bIndex, e)} />
                                        <button className="btn btn-outline-danger" type="button" onClick={() => handleRemoveBullet('education', index, bIndex)}>
                                            <i className="fas fa-minus"></i>
                                        </button>
                                    </div>
                                ))}
                                <button className="btn btn-sm btn-outline-primary mt-2" onClick={() => handleAddBullet('education', index)}>
                                    Add Detail
                                </button>
                                {resumeData.education.length > 1 && (
                                    <button className="btn btn-sm btn-danger position-absolute top-0 end-0 m-2" onClick={() => handleRemoveEntry('education', index)}>
                                        <i className="fas fa-times"></i>
                                    </button>
                                )}
                            </div>
                        ))}
                        <button className="btn btn-primary mt-3" onClick={() => handleAddEntry('education')}>
                            Add Education
                        </button>
                    </div>
                </div>

                {/* Resume Preview Section */}
                <div className="col-lg-6">
                    <div className="d-flex justify-content-end mb-3 mx-3">
                        <button
                            className="btn btn-primary btn-lg fs-16"
                            onClick={handleDownloadPdf}
                            disabled={!isHtml2PdfLoaded}
                        >
                            <i className="fas fa-download me-2"></i>
                            {isHtml2PdfLoaded ? 'Download Resume PDF' : 'Loading PDF Generator...'}
                        </button>
                    </div>
                    <div className="resume-preview-container" id="resume-preview-content">
                        {/* Header Section */}
                        <div className="resume-header-section">
                            <img src={resumeData.personalInfo.imageUrl} alt="Profile" onError={(e) => e.target.src = 'https://placehold.co/150x150/E0E0E0/000000?text=Profile'} />
                            <div className="resume-header-info">
                                <h2>{resumeData.personalInfo.name || 'Your Name'}</h2>
                                <h3>{resumeData.personalInfo.title || 'Your Title'}</h3>
                                <p>{resumeData.personalInfo.address}</p>
                                <p>{resumeData.personalInfo.phone} | {resumeData.personalInfo.email}</p>
                            </div>
                        </div>

                        <div className="resume-content">
                            {/* Left Column */}
                            <div className="resume-left-column">
                                {/* Skills */}
                                <div className="resume-section mb-4">
                                    <h4 className="resume-section-title">Skills</h4>
                                    <ul className='mx-3'>
                                        {resumeData.skills.map((skill, index) => (
                                            <li key={index}>{skill}</li>
                                        ))}
                                    </ul>
                                </div>

                                {/* Languages */}
                                <div className="resume-section mb-4">
                                    <h4 className="resume-section-title">Languages</h4>
                                    <ul className='mx-3'>
                                        {resumeData.languages.map((lang, index) => (
                                            <li key={index}>{lang}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>

                            {/* Right Column */}
                            <div className="resume-right-column">
                                {/* Profile Summary */}
                                <div className="resume-section mb-4">
                                    <h4 className="resume-section-title">Profile</h4>
                                    <p className="profile-summary-text">{resumeData.profileSummary}</p>
                                </div>

                                {/* Employment History */}
                                <div className="resume-section mb-4">
                                    <h4 className="resume-section-title">Employment History</h4>
                                    {resumeData.employmentHistory.map((entry, index) => (
                                        <div key={index} className="employment-item">
                                            <h4>{entry.title}</h4>
                                            <p>{entry.company}</p>
                                            <p>{entry.startDate} – {entry.endDate}</p>
                                            <ul>
                                                {entry.responsibilities.map((resp, rIndex) => (
                                                    <li key={rIndex}>{resp}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    ))}
                                </div>

                                {/* Education */}
                                <div className="resume-section mb-4">
                                    <h4 className="resume-section-title">Education</h4>
                                    {resumeData.education.map((entry, index) => (
                                        <div key={index} className="education-item">
                                            <h4>{entry.degree}</h4>
                                            <p>{entry.institution}</p>
                                            <p>{entry.startDate} – {entry.endDate}</p>
                                            <ul>
                                                {entry.details.map((detail, dIndex) => (
                                                    <li key={dIndex}>{detail}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResumeBuilder;
